import React, { useCallback } from 'react';
import { styled } from '@compiled/react';

import { Stack } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { usePublish } from '@atlaskit/rovo-triggers';
import { useIntl } from '@atlassian/jira-intl';

import { FollowUpButton } from '@atlassian/conversation-assistant-ui-components';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import type { FollowUpObject } from '@atlassian/ai-agent-streaming';
import { useSmartSummarizeCommentsAnalytics } from '@atlassian/jira-issue-smart-request-summary-state/src/common/analytics/index.tsx';
import { useIsProactivePanelShown } from '@atlassian/jira-issue-smart-request-summary-state/src/controllers/state/index.tsx';
import messages from './messages.tsx';

type Props = {
	followUpContent: FollowUpObject[];
	summaryContent: string;
	onClose: () => void;
};

const getContentIssueKeyUrl = (issueKey: string) => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	return `${window.location.origin}/browse/${issueKey}`;
};

export const FollowUps = ({ followUpContent, summaryContent, onClose }: Props) => {
	const publish = usePublish('ai-mate');
	const { formatMessage } = useIntl();
	const issueKey = useIssueKey();
	const { fireTrack } = useSmartSummarizeCommentsAnalytics();
	const [isProactivePanelShown] = useIsProactivePanelShown();

	// allow us to distinguish whether proactive vs non-proactive in the rovo-chat analytics events
	const experienceName = isProactivePanelShown
		? 'proactive-jira-smart-summary'
		: 'jira-smart-summary';

	const onFollowUpClick = useCallback(
		(followUp: FollowUpObject) => {
			publish({
				type: 'chat-new',
				source: experienceName,
				data: {
					name: followUp.text.substring(0, 30),
					dialogues: [
						{
							human_message: {
								content: formatMessage(messages.followUpUserQuery, {
									link: getContentIssueKeyUrl(issueKey),
								}),
							},
							agent_message: { content: summaryContent },
						},
					],
					prompt: followUp.text,
				},
			});
			fireTrack('aiResult actioned', 'followUpQueryButton', {
				aiResultAction: 'followUpQuestionClick',
			});
			onClose();
		},
		[publish, experienceName, formatMessage, issueKey, summaryContent, fireTrack, onClose],
	);

	return (
		<Stack
			space="space.100"
			testId="issue-smart-request-summary.ui.follow-ups.comment-summary-follow-ups"
		>
			{followUpContent.map((followUp, index) => (
				<FollowUpButtonWrapper key={index}>
					<FollowUpButton
						key={followUp.text}
						followUpIndex={index}
						followUpText={followUp.text}
						followUpType={followUp.type}
						experienceId={experienceName}
						onClick={() => onFollowUpClick(followUp)}
					/>
				</FollowUpButtonWrapper>
			))}
		</Stack>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const FollowUpButtonWrapper = styled.div({
	// This is necessary due to a bug (DSP-19088) with the Split Button,
	// but we won't need it for long as the button will soon be replaced.

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		borderEndEndRadius: `${token('border.radius.050')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		borderStartEndRadius: `${token('border.radius.200')} !important`,
		width: '100%',
	},
});
